import { useEffect, useState } from 'react';
import { usePermission } from '../../../../../auth/AuthContext';
import { Select } from '../../../../../components/inputs/Select';
import type { ApiError } from '../../../../../lib/apiError';
import { orgUnitsApi } from '../../../organization/organizationApi';
import type { OrgUnitOption } from '../../../organization/organizationTypes';
import { plantillaApi } from '../../../plantilla/plantillaApi';
import type { PlantillaPositionListItem } from '../../../plantilla/plantillaTypes';
import { positionsApi } from '../../../positions/positionsApi';
import type { PositionOption } from '../../../positions/positionTypes';
import type { PlantillaVacantItem } from '../../personnelTypes';
import { vacantSlotOptions } from '../../utils/vacantPositionOptions';

/**
 * UNIT → POSITION → ITEM, the one place the question is asked.
 *
 * **Both forms that post somebody to a seat render this.** `<PersonnelModal>`'s Employment step
 * and `<EmploymentRecordModal>` ask one question, and they used to ask it with two copies of the
 * same pair of pickers — which is how they drifted apart the moment one of them grew a third.
 * A reader who filled the wizard finds the service-record form behaving identically because it
 * is the same component, not because somebody remembered.
 *
 * **It asks for the UNIT first** — product call, 19 Oct 2026, reversing the merged picker of
 * 11 Sep. A reader posting somebody names the office first, and the unit is what decides whether
 * the designation they want is authorized there at all: the question neither form could ask
 * while the unit and the title were one value.
 *
 * **Nothing here writes.** Both the post and the item are STAGED — held behind a sentinel until
 * the parent form saves, and written by {@link resolveStagedItem} immediately before the record.
 * Firing on the press left junk rows behind from forms that were never submitted.
 */

/*
  THE POSITION PICKER CARRIES TWO KINDS OF ANSWER, so each says which it is.

  A post already authorized in this unit is chosen by its POST id; a designation the unit has no
  post for is chosen by its CATALOG id, and picking one stages a post to be authorized when the
  form is submitted. They cannot share one namespace: a legacy post predating the catalog link
  carries no title id at all, so keying everything on the designation would drop exactly the
  posts somebody is most likely to be posted into.
*/
const AUTHORIZED_POST = 'post:';
const NEW_POST = 'title:';

/** A slot TYPED into the picker and not written yet — the prefix plus the item number, verbatim. */
const STAGED_SLOT = '__new-slot__:';

/** What the three pickers hold between them. One value, so a caller cannot keep half of it. */
export interface PostSelection {
  orgUnitId: string | null;

  /** A post (`post:<id>`), or a designation the unit has none for (`title:<id>`). */
  postChoice: string | null;

  /** A real item id, or `__new-slot__:<item number>` for one the reader typed. */
  itemId: string | null;
}

export const EMPTY_POST_SELECTION: PostSelection = {
  orgUnitId: null,
  postChoice: null,
  itemId: null,
};

/** Every part answered. The server re-checks what was chosen; this only says the form is filled. */
export function postSelectionValid(selection: PostSelection): boolean {
  return (
    selection.orgUnitId !== null && selection.postChoice !== null && selection.itemId !== null
  );
}

/**
 * The trio, read off ONE item in the vacant feed.
 *
 * What Autogenerate fills from: choosing a unit, a designation and an item independently can
 * name a seat that does not belong to the post above it, which the server refuses. The feed
 * carries the unit and the post, so the three cannot disagree.
 */
export function postSelectionOf(item: PlantillaVacantItem): PostSelection {
  return {
    orgUnitId: item.orgUnitId,
    postChoice: `${AUTHORIZED_POST}${item.plantillaPositionId}`,
    itemId: item.id,
  };
}

/** The typed item number behind a staged slot, or null for a real one. */
export function stagedItemNumber(itemId: string | null): string | null {
  return itemId !== null && itemId.startsWith(STAGED_SLOT)
    ? itemId.slice(STAGED_SLOT.length)
    : null;
}

/**
 * Writes whatever the selection staged, and answers with the REAL item id to post against.
 *
 * **Called immediately before the parent's own write, never on the picker's press.** Both calls
 * are the plantilla's own, under the plantilla's own keys: folding either into the personnel
 * request would be one key silently performing a write another governs.
 *
 * Residual gap, stated rather than hidden: a record refused AFTER this leaves the new post or
 * the new seat behind, vacant — one request wide, not the whole time the form was open.
 */
export async function resolveStagedItem(selection: PostSelection): Promise<string | null> {
  const typed = stagedItemNumber(selection.itemId);

  // Nothing staged: the reader picked a seat that already exists.
  if (typed === null) return selection.itemId;

  const postId = selection.postChoice?.startsWith(AUTHORIZED_POST)
    ? selection.postChoice.slice(AUTHORIZED_POST.length)
    : null;

  if (postId !== null) {
    const slots = await plantillaApi.addSlots(postId, [typed]);

    return slots.find((slot) => slot.itemNumber.toUpperCase() === typed.toUpperCase())?.id ?? null;
  }

  const titleId = selection.postChoice?.startsWith(NEW_POST)
    ? selection.postChoice.slice(NEW_POST.length)
    : null;

  if (titleId === null || selection.orgUnitId === null) return null;

  /*
    THE POST ITSELF IS AUTHORIZED HERE — the reverse of leaving the reader to open the Plantilla
    module, add the post and come back. It is the plantilla's own create with the typed item
    number as the post's first seat, so the staffing pattern gains exactly the row it would have
    gained there and the Plantilla list shows it.
  */
  /*
    ONE UNIT, one item number. The create takes a LIST of units (19 Oct 2026) because the
    plantilla form authorizes a designation across several at once; this form posts ONE person
    into ONE seat, so it sends the one card and reads back the one post.
  */
  const [post] = await plantillaApi.create({
    positionId: titleId,
    units: [{ orgUnitId: selection.orgUnitId, itemNumbers: [typed] }],
  });

  if (post === undefined) return null;

  // The create answers with the POST, not its items, so the seat is read back by number.
  const slots = await plantillaApi.slots(post.id);

  return slots.find((slot) => slot.itemNumber.toUpperCase() === typed.toUpperCase())?.id ?? null;
}

export function PostPickers({
  idPrefix,
  value,
  onChange,
  vacantItems,
  error,
  disabled = false,
}: {
  /** The form's own id prefix, so a rejected field resolves to a control that exists. */
  idPrefix: string;
  value: PostSelection;
  onChange: (next: PostSelection) => void;

  /** The vacant feed the parent already loaded — the slots each post has open. */
  vacantItems: PlantillaVacantItem[];
  error?: ApiError | null;
  disabled?: boolean;
}) {
  /**
   * Adding a seat to a post that EXISTS — a headcount change, which is the plantilla's key and
   * not this form's.
   */
  const canAddSlot = usePermission('plantilla.manage-slots');

  /**
   * Authorizing a POST. Separate from the one above, and a reader can hold either without the
   * other: adding a seat is a headcount change, where authorizing a post is a new line in the
   * staffing pattern. Without it the picker offers only what the unit already has.
   */
  const canCreatePost = usePermission('plantilla.create');

  const [units, setUnits] = useState<OrgUnitOption[]>([]);
  const [positions, setPositions] = useState<PositionOption[]>([]);

  /** Every active post in the chosen unit — what says whether the designation is authorized. */
  const [unitPosts, setUnitPosts] = useState<PlantillaPositionListItem[]>([]);
  const [loadingUnitPosts, setLoadingUnitPosts] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    orgUnitsApi
      .options(undefined, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setUnits(next);
      })
      .catch(() => {
        // The picker renders empty and the form says so; a failed catalog load names no field.
      });

    positionsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setPositions(next);
      })
      .catch(() => {
        // Without the catalog the form can still post into a seat that exists.
      });

    return () => controller.abort();
  }, []);

  /*
    THE UNIT'S POSTS, fetched per unit rather than filtered out of the vacant feed: that feed
    carries only items with a seat open, so a post whose every slot is filled would read as "not
    authorized here" and the form would offer to create a second post the server refuses.
  */
  useEffect(() => {
    if (value.orgUnitId === null) {
      setUnitPosts([]);
      return;
    }

    const controller = new AbortController();
    setLoadingUnitPosts(true);

    plantillaApi
      .list({ unit: value.orgUnitId, status: 'active', pageSize: 100 }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setUnitPosts(page.items);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;

        // Empty rather than stale: another unit's list would answer "already authorized" here.
        setUnitPosts([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingUnitPosts(false);
      });

    return () => controller.abort();
  }, [value.orgUnitId]);

  const chosenPostId = value.postChoice?.startsWith(AUTHORIZED_POST)
    ? value.postChoice.slice(AUTHORIZED_POST.length)
    : null;

  const newPostTitleId = value.postChoice?.startsWith(NEW_POST)
    ? value.postChoice.slice(NEW_POST.length)
    : null;

  const existingPost =
    chosenPostId === null ? null : (unitPosts.find((post) => post.id === chosenPostId) ?? null);

  /**
   * The unit's posts have arrived, so "this designation has no post here" is a FACT rather than
   * a list that has not loaded. Without it the form would offer to authorize a post that is
   * already there, for as long as the fetch takes.
   */
  const unitResolved = value.orgUnitId !== null && !loadingUnitPosts;

  /*
    A post with no seat open is still listed for a reader who may add one, because typing an item
    number is how a seat is authorized from here. Without that key it would be a control that can
    only produce a rejection, so it is left out.
  */
  const authorizedPosts = unitPosts.filter((post) => canAddSlot || post.vacantSlots > 0);

  const authorizedTitleIds = new Set(
    unitPosts.map((post) => post.positionId).filter((id): id is string => id != null),
  );

  const unauthorizedTitles = canCreatePost
    ? positions.filter((title) => !authorizedTitleIds.has(title.id))
    : [];

  const postOptions = [
    ...(authorizedPosts.length > 0
      ? [{ value: 'header-authorized', label: 'Authorized in this unit', header: true }]
      : []),
    ...authorizedPosts.map((post) => ({
      value: `${AUTHORIZED_POST}${post.id}`,
      label: post.title,

      // The code identifies the post to the plantilla and to nobody else, so it reads under the
      // designation rather than as the label — and stays searchable.
      sublabel: post.code,
      keywords: post.code,
      right: <span className="font-mono tabular-nums">{post.vacantSlots} vacant</span>,
    })),
    ...(unauthorizedTitles.length > 0
      ? [{ value: 'header-unauthorized', label: 'No post in this unit yet', header: true }]
      : []),
    ...unauthorizedTitles.map((title) => ({
      value: `${NEW_POST}${title.id}`,
      label: title.title,
      sublabel: title.code,
      keywords: title.code,
    })),
  ];

  const typed = stagedItemNumber(value.itemId);

  return (
    <>
      {/*
        THE UNIT LEADS. It is the question the two below depend on: which designations are worth
        posting into, and whether one is authorized here at all, are both answered per unit.
      */}
      <Select
        id={`${idPrefix}-org-unit-id`}
        label="Unit"
        required
        remote
        description="The office this post sits in. Managed under Organization."
        value={value.orgUnitId}
        onChange={(next) =>
          /*
            Both answers below belong to the unit — a post is authorized IN one — so moving unit
            clears them rather than carrying a post from the office the reader just left.
          */
          onChange({ orgUnitId: next || null, postChoice: null, itemId: null })
        }
        options={units.map((unit) => ({
          value: unit.id,
          label: unit.name,
          right: unit.orgUnitTypeName,
        }))}
        error={error?.fieldError('orgUnitId')}
        disabled={disabled}
      />

      {/*
        THE POSITION, answering with one of two things — see the two prefixes. Drawn always and
        DISABLED until a unit is chosen: a required field appearing from nowhere moves every
        field under it, so it says WHY through its own description instead.
      */}
      <Select
        id={`${idPrefix}-plantilla-position-id`}
        label="Position"
        required
        remote
        description={
          value.orgUnitId === null
            ? 'Choose a unit first — a post is authorized in a unit.'
            : canCreatePost
              ? 'The post being filled. A designation this unit has no post for is authorized when you submit.'
              : 'The posts this unit has authorized.'
        }
        value={value.postChoice}
        onChange={(next) =>
          // The slot belongs to the post; keeping it across a change would write a seat in a job
          // the reader just moved off.
          onChange({ ...value, postChoice: next || null, itemId: null })
        }
        loading={loadingUnitPosts}
        options={postOptions}
        sort="none"
        error={error?.fieldError('plantillaPositionId')}
        disabled={disabled || value.orgUnitId === null}
      />

      {/*
        THE SLOT, narrowed to the post. Product call, 15 Sep 2026: the two were one picker and
        the server took the lowest-numbered open seat, which is the right default and the wrong
        rule once an employee can hold several posts.

        The reader NAMES it — the typed text IS the item number (16 Oct 2026) — so the create row
        quotes what they typed. Which key that needs depends on the answer above: adding a seat to
        a post that exists is a headcount change, where the first seat of a staged post rides on
        the post's own create.
      */}
      <Select
        id={`${idPrefix}-plantilla-item-id`}
        label="Plantilla slot"
        required
        description={
          value.postChoice === null
            ? 'Choose a position first — the slots belong to it.'
            : newPostTitleId !== null
              ? 'This unit has no post for that designation yet. Type the item number it is authorized with — the post and the item are created when you submit.'
              : canAddSlot
                ? 'The item number this is written against. Type a new item number to add a slot to this post — it is created when you submit.'
                : 'The item number this is written against.'
        }
        value={value.itemId}
        onChange={(next) => onChange({ ...value, itemId: next || null })}
        options={[
          ...vacantSlotOptions(vacantItems, existingPost?.id ?? null),

          // A STAGED slot is offered under its typed number until the save writes it.
          ...(typed === null ? [] : [{ value: value.itemId!, label: typed }]),
        ]}
        sort="none"
        allowCustom={existingPost !== null ? canAddSlot : newPostTitleId !== null}
        onCreateOption={(raw) => {
          const number = raw.trim();
          if (number.length === 0) return;

          onChange({ ...value, itemId: `${STAGED_SLOT}${number}` });
        }}
        error={error?.fieldError('plantillaItemId')}
        disabled={disabled || value.postChoice === null || !unitResolved}
      />

      {/*
        Said on the form rather than left to an empty dropdown: with nothing to post into and no
        key to authorize anything, there is nothing this reader can do on this screen.
      */}
      {unitResolved && postOptions.length === 0 ? (
        <p className="text-xs text-slate-500">
          This unit has authorized no post you can fill. Add one in Plantilla first, or choose
          another unit.
        </p>
      ) : null}
    </>
  );
}
